import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { ReactNode, useState } from "react";

/** Width of the collapsed rail. Wide enough for a 34px icon button plus its focus ring. */
const SLIVER_WIDTH = 44;

/**
 * The column's height, handed down rather than left to the content.
 *
 * The panel inside ends in a paginator, and MRT drops that to the foot of its own paper with a
 * `margin-top: auto` — which moves it only while the paper has height left over to give. This
 * wrapper and `PanelBody` below it were plain blocks at their content height, so the paper was
 * exactly as tall as its rows and the paginator sat directly under the last template instead of at
 * the bottom of the column.
 *
 * A percentage against a parent with no definite height of its own resolves to `auto`, which is what
 * keeps this harmless on a compact pane: there the column is a row in a stack rather than a column
 * beside the tab, and the accordion is deliberately `height: auto`.
 */
const Panel = styled("div", { shouldForwardProp: (prop) => prop !== "minimized" })<{ minimized: boolean }>(
  ({ theme, minimized }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: "initial",
    ...(minimized && {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      gap: theme.spacing(0.5),
      overflow: "hidden",
      paddingTop: theme.spacing(1),
      width: SLIVER_WIDTH,
    }),
  }),
);
/**
 * Holds the panel itself, and takes the rest of the column — see `Panel`.
 *
 * The fill is written under `:not([hidden])` rather than on the element, because `hidden` is a UA
 * `display: none` and any author `display` here would outrank it: declared plainly, this would go on
 * drawing the whole panel behind the collapsed rail.
 */
const PanelBody = styled("div")({
  "&:not([hidden])": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
});
const PeekHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  paddingRight: theme.spacing(1),
}));
const SliverLabel = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "0.75rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  writingMode: "vertical-rl",
}));

type CollapsibleTemplatePanelProps = {
  /** When true the panel gives up its column and shows the rail instead — the side pane needs the room. */
  minimized: boolean;
  label?: string;
  selectedCount?: number;
  children: ReactNode;
};

/**
 * The templates column of a proposal tab. Opening the comments/selection side pane used to hide this
 * outright, which left no way back to the templates without closing the pane; now it collapses to a
 * labelled rail that can be expanded back over the narrowed layout for as long as the pane stays open.
 */
export default function CollapsibleTemplatePanel(props: Readonly<CollapsibleTemplatePanelProps>) {
  const { minimized, children } = props;
  const label = props.label ?? "Templates";
  const selectedCount = props.selectedCount ?? 0;

  const [wasMinimized, setWasMinimized] = useState(minimized);
  const [isPeeking, setIsPeeking] = useState(false);
  // A peek belongs to the side-pane session that prompted it: reopening the pane starts back at the rail.
  if (wasMinimized !== minimized) {
    setWasMinimized(minimized);
    setIsPeeking(false);
  }

  const showSliver = minimized && !isPeeking;
  const lowerLabel = label.toLowerCase();

  return (
    <Panel minimized={showSliver}>
      {showSliver && (
        <>
          <Tooltip title={`Show ${lowerLabel}`} placement="right">
            <IconButton aria-label={`Show ${lowerLabel}`} color="primary" size="small" onClick={() => setIsPeeking(true)}>
              <Badge badgeContent={selectedCount} color="primary" overlap="circular">
                <ChevronRightIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <SliverLabel>{label}</SliverLabel>
        </>
      )}
      {minimized && isPeeking && (
        <PeekHeader>
          <Tooltip title={`Hide ${lowerLabel}`} placement="left">
            <IconButton aria-label={`Hide ${lowerLabel}`} color="primary" size="small" onClick={() => setIsPeeking(false)}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        </PeekHeader>
      )}
      <PanelBody hidden={showSliver}>{children}</PanelBody>
    </Panel>
  );
}
